import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import {
  createAIPortalRequest,
  getAgentPortalConfig,
  getAIPortalRequest,
  getAIPortalRequests
} from '../services/aiPortalService';
import './AdminDashboard.css';
import './AdminDashboardModern.css';
import './AgentWinds.css';

const POLL_INTERVAL = 5000;
const LONG_RUNNING_MS = 60000;
const TIMEOUT_MS = 300000;
const LOADING_MESSAGE_INTERVAL = 3500;

// Rotating status lines shown while a request polls. Keyed by request_type so
// the copy matches what the AI worker is actually doing. Both client-lead and
// aw-biz-lead submit request_type 'lead', so one set covers both.
const LOADING_MESSAGES = {
  lead: [
    'Scanning county records for motivated sellers...',
    "Checking foreclosure notices — someone's loss could be your client's gain.",
    'Digging through FSBO listings for unrepresented sellers...',
    'Cross-referencing public notices with property records...',
    'Filtering out leads you have already seen — no repeats, ever.',
    'Searching Facebook groups for buyers venting about the market...',
    "Checking Craigslist for 'must sell' language...",
    'Almost there — ranking the best candidate by intent...'
  ],
  'x-check': [
    'Scanning X for people talking about real estate AI...',
    "Someone's always complaining about their IDX. Let's find them.",
    'Crafting replies under 10 words — brevity is the soul of wit.',
    'Checking for mentions of @Agent_Winds...',
    'Found a few conversations. Deciding which ones are worth a reply...'
  ],
  'x-post': [
    'Deciding what to post today — AI education or a speed flex?',
    "Drafting something that doesn't sound like a bot wrote it...",
    'Choosing the right angle — stop browsing vs. show don\'t sell...',
    'Polishing the tweet. Yes, we draft and redraft. We have standards.'
  ],
  'seo-track': [
    'Googling ourselves so you don\'t have to...',
    "Checking where we rank for 'AI IDX' — brace yourself.",
    'Scanning for competitors who showed up this week...',
    'Finding content gaps we should be blogging about...',
    "Comparing last week's positions to this week's..."
  ],
  'aw-blog': [
    'Picking a keyword that actually has search volume...',
    'Writing something a real person would want to read...',
    'Generating an image that doesn\'t look like AI slop...',
    'Updating the sitemap — Google loves a fresh sitemap.',
    'Triggering an Amplify rebuild. This is the boring part.',
    'Final checks — does this actually answer the question?'
  ],
  default: [
    'The AI is doing its thing. This usually takes 30-60 seconds.',
    'Reading data, making decisions, being useful...',
    'Behind the scenes: search, filter, rank, deliver.',
    'Good things come to those who poll.',
    'Working on it — no lead left behind.'
  ]
};

const formatKey = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'None';
  return String(value);
};

const formatRequestDate = (value) => {
  if (!value) return 'No date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
};

const getRequestTitle = (request) => {
  const lead = request?.ai_response?.leads?.[0];
  if (lead?.name) return lead.name;
  if (request?.ai_response?.summary) return request.ai_response.summary;
  if (request?.request_detail) return request.request_detail;
  return formatKey(request?.request_type || 'Agent Winds Request');
};

const RecentRequests = ({ requests, loading, error, onSelect }) => (
  <section className="agent-winds-history" aria-labelledby="agent-winds-history-title">
    <div className="agent-winds-history-header">
      <div>
        <p className="agent-winds-kicker">History</p>
        <h2 id="agent-winds-history-title">Recent Requests</h2>
      </div>
      <span>Last 5</span>
    </div>

    {loading && <p className="agent-winds-history-muted">Loading recent requests...</p>}
    {!loading && error && <p className="agent-winds-history-muted">{error}</p>}
    {!loading && !error && requests.length === 0 && (
      <p className="agent-winds-history-muted">No previous requests for this site yet.</p>
    )}

    {!loading && !error && requests.length > 0 && (
      <div className="agent-winds-history-list">
        {requests.map((request) => (
          <button
            type="button"
            className="agent-winds-history-row"
            key={request.id}
            onClick={() => onSelect(request)}
          >
            <span className={`agent-winds-status-dot ${request.status === 'complete' ? 'complete' : 'processing'}`} />
            <span className="agent-winds-history-main">
              <strong>{getRequestTitle(request)}</strong>
              <small>#{request.id} · {formatRequestDate(request.created_at)} · {formatKey(request.request_type || 'request')}</small>
            </span>
            <span className={`agent-winds-history-status ${request.status === 'complete' ? 'complete' : 'processing'}`}>
              {request.status || 'pending'}
            </span>
          </button>
        ))}
      </div>
    )}
  </section>
);

const LeadResults = ({ response }) => {
  const leads = response?.leads || [];

  return (
    <section className="agent-winds-result-panel agent-winds-fade-in" aria-labelledby="agent-winds-results-title">
      <div className="agent-winds-result-header">
        <p className="agent-winds-kicker">Lead Finder</p>
        <h2 id="agent-winds-results-title">Request Complete</h2>
        {response?.summary && <p>{response.summary}</p>}
      </div>

      {leads.length === 0 ? (
        <div className="agent-winds-empty-result">
          <h3>No leads found this time</h3>
          <p>{response?.summary || 'The AI worker completed the search but did not return a lead.'}</p>
        </div>
      ) : (
        <div className="agent-winds-lead-grid">
          {leads.map((lead, index) => (
            <article className="agent-winds-lead-card" key={`${lead.name}-${index}`}>
              <h3>{lead.name || 'Unnamed Lead'}</h3>
              <div className="agent-winds-contact-actions">
                {lead.phone && (
                  <a href={`tel:${lead.phone}`} className="agent-winds-contact-button">
                    {lead.phone}
                  </a>
                )}
                {lead.email && (
                  <a href={`mailto:${lead.email}`} className="agent-winds-contact-button secondary">
                    {lead.email}
                  </a>
                )}
              </div>
              {lead.reason && <p>{lead.reason}</p>}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const DataResults = ({ response }) => {
  const data = response?.data || {};
  const workflow = response?.workflow;

  if (workflow === 'aw-x-check') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <div className="agent-winds-metric-row">
          <Metric label="Tweets Found" value={data.tweets_found} />
          <Metric label="Replies Posted" value={data.replies_posted} />
        </div>
        <div className="agent-winds-list-stack">
          {(data.replies || []).map((reply, index) => (
            <article className="agent-winds-data-card" key={`${reply.tweet_url}-${index}`}>
              <h3>Reply {index + 1}</h3>
              {reply.original_tweet && <p>{reply.original_tweet}</p>}
              {reply.reply_text && <blockquote>{reply.reply_text}</blockquote>}
              {reply.tweet_url && <a href={reply.tweet_url} target="_blank" rel="noreferrer">View Tweet</a>}
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (workflow === 'aw-x-post') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <article className="agent-winds-data-card">
          <h3>Published Tweet</h3>
          {data.tweet_text && <p>{data.tweet_text}</p>}
          <div className="agent-winds-badge-row">
            <span>{formatValue(data.content_type)}</span>
            <span>{data.is_thread ? 'Thread' : 'Single Post'}</span>
          </div>
          {data.tweet_url && <a href={data.tweet_url} target="_blank" rel="noreferrer">View Tweet</a>}
        </article>
      </section>
    );
  }

  if (workflow === 'aw-seo-track') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <div className="agent-winds-table-wrap">
          <table className="agent-winds-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Position</th>
                <th>Top Competitors</th>
              </tr>
            </thead>
            <tbody>
              {(data.rankings || []).map((ranking, index) => (
                <tr key={`${ranking.keyword}-${index}`}>
                  <td>{ranking.keyword}</td>
                  <td>{ranking.position}</td>
                  <td>{(ranking.top_competitors || []).join(', ') || 'None'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.recommended_blog_topic && (
          <article className="agent-winds-data-card">
            <h3>Recommended Topic</h3>
            <p>{data.recommended_blog_topic}</p>
            <span className="agent-winds-pill">Movement: {formatValue(data.movement_vs_last_week)}</span>
          </article>
        )}
      </section>
    );
  }

  if (workflow === 'aw-blog') {
    return (
      <section className="agent-winds-result-panel agent-winds-fade-in">
        <ResultSummary response={response} />
        <article className="agent-winds-data-card">
          <h3>{data.title || 'Blog Published'}</h3>
          {data.url && <a href={data.url} target="_blank" rel="noreferrer">{data.url}</a>}
          <div className="agent-winds-badge-row">
            <span>Image: {formatValue(data.image_uploaded)}</span>
            <span>Sitemap: {formatValue(data.sitemap_updated)}</span>
            <span>Rebuild: {formatValue(data.amplify_rebuild_triggered)}</span>
          </div>
          {data.keyword_source && <p>Keyword source: {data.keyword_source}</p>}
        </article>
      </section>
    );
  }

  return (
    <section className="agent-winds-result-panel agent-winds-fade-in">
      <ResultSummary response={response} />
      <div className="agent-winds-list-stack">
        {Object.entries(data).map(([key, value]) => (
          <article className="agent-winds-data-card" key={key}>
            <h3>{formatKey(key)}</h3>
            <pre>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
          </article>
        ))}
      </div>
    </section>
  );
};

const ResultSummary = ({ response }) => (
  <div className="agent-winds-result-header">
    <p className="agent-winds-kicker">{response?.workflow || 'Agent Winds'}</p>
    <h2>Request Complete</h2>
    {response?.summary && <p>{response.summary}</p>}
  </div>
);

const Metric = ({ label, value }) => (
  <div className="agent-winds-metric">
    <strong>{formatValue(value)}</strong>
    <span>{label}</span>
  </div>
);

const LoadingState = ({ elapsedMs, requestType }) => {
  const messages = useMemo(
    () => LOADING_MESSAGES[requestType] || LOADING_MESSAGES.default,
    [requestType]
  );
  const [msgIndex, setMsgIndex] = useState(() => Math.floor(Math.random() * messages.length));

  useEffect(() => {
    setMsgIndex(Math.floor(Math.random() * messages.length));
    if (messages.length <= 1) return undefined;

    const interval = setInterval(() => {
      setMsgIndex((prev) => {
        let next = prev;
        while (next === prev) {
          next = Math.floor(Math.random() * messages.length);
        }
        return next;
      });
    }, LOADING_MESSAGE_INTERVAL);

    return () => clearInterval(interval);
  }, [messages]);

  const isLong = elapsedMs > LONG_RUNNING_MS;

  return (
    <section className="agent-winds-loading" role="status" aria-live="polite">
      <div className="agent-winds-spinner" aria-hidden="true" />
      <div>
        <h2>Agent Winds is working</h2>
        <p key={msgIndex} className="agent-winds-loading-message agent-winds-fade-in">
          {messages[msgIndex]}
        </p>
        <span>
          {Math.floor(elapsedMs / 1000)}s elapsed
          {isLong ? ' · still working — big jobs can take up to 30 minutes; you can safely leave this page' : ''}
        </span>
      </div>
      <div className="agent-winds-skeleton-grid" aria-hidden="true">
        <div />
        <div />
        <div />
      </div>
    </section>
  );
};

const AgentWinds = () => {
  const navigate = useNavigate();
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const startedAtRef = useRef(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [requestDetail, setRequestDetail] = useState('');
  const [requestState, setRequestState] = useState('idle');
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [activeRequestType, setActiveRequestType] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recentRequests, setRecentRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  // Daily task quota. The limit comes from the agent's global-config entry
  // (daily_task_quota, default 1). Failed runs don't count, so a client whose
  // task errored can try again. Hermes enforces the same limit server-side;
  // this is the friendly front-end gate.
  const [tasksToday, setTasksToday] = useState(0);

  const loadRecentRequests = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const requests = await getAIPortalRequests(SITE_CONFIG.SITE_ID);
      const today = new Date().toDateString();
      setTasksToday(requests.filter((r) =>
        r.created_at &&
        new Date(r.created_at).toDateString() === today &&
        ['processing', 'complete'].includes(r.status)
      ).length);
      const sortedRequests = requests
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5);
      setRecentRequests(sortedRequests);
    } catch (err) {
      setHistoryError(err.message || 'Unable to load recent requests.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      clearTimers();
    };
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setConfigLoading(true);
        const config = await getAgentPortalConfig(SITE_CONFIG.SITE_ID);
        setPortalConfig(config);
        setSelectedWorkflowId(config.workflows[0]?.id || '');
      } catch (err) {
        setError(err.message || 'Unable to load Agent Winds configuration.');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
    loadRecentRequests();
  }, [loadRecentRequests]);

  const selectedWorkflow = useMemo(() => (
    portalConfig?.workflows?.find((workflow) => workflow.id === selectedWorkflowId)
  ), [portalConfig, selectedWorkflowId]);

  const clearTimers = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    pollTimerRef.current = null;
    elapsedTimerRef.current = null;
  };

  const startElapsedTimer = () => {
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 1000);
  };

  const pollRequest = async (requestId) => {
    try {
      const current = await getAIPortalRequest(requestId, SITE_CONFIG.SITE_ID);
      if (current.status === 'complete') {
        clearTimers();
        setResult(current);
        setRequestState('complete');
        loadRecentRequests();
        return;
      }

      if (Date.now() - startedAtRef.current >= TIMEOUT_MS) {
        clearTimers();
        setRequestState('timeout');
        setError('This is still running in the background — some tasks take up to 30 minutes. You can safely leave this page; the result will appear in Recent Requests when it finishes.');
        return;
      }

      pollTimerRef.current = setTimeout(() => pollRequest(requestId), POLL_INTERVAL);
    } catch (err) {
      clearTimers();
      setRequestState('error');
      setError(err.message || 'Unable to check the request status.');
    }
  };

  const dailyQuota = Number(portalConfig?.agent?.daily_task_quota) >= 1
    ? Number(portalConfig.agent.daily_task_quota)
    : 1;
  const usedToday = tasksToday >= dailyQuota;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedWorkflow || requestState === 'processing' || usedToday) return;

    try {
      clearTimers();
      setError('');
      setResult(null);
      setRequestState('processing');
      setActiveRequestType(selectedWorkflow.request_type);
      startElapsedTimer();

      const created = await createAIPortalRequest({
        siteid: SITE_CONFIG.SITE_ID,
        requester: selectedWorkflow.requester,
        request_type: selectedWorkflow.request_type,
        request_detail: requestDetail || selectedWorkflow.id
      });

      setActiveRequestId(created.id);
      loadRecentRequests();
      pollRequest(created.id);
    } catch (err) {
      clearTimers();
      setRequestState('error');
      setError(err.message || 'Unable to create the Agent Winds request.');
    }
  };

  const handleReset = () => {
    clearTimers();
    setRequestState('idle');
    setResult(null);
    setError('');
    setActiveRequestId(null);
    setElapsedMs(0);
  };

  const handleCheckStatusLater = () => {
    if (!activeRequestId) return;
    clearTimers();
    setError('');
    setRequestState('processing');
    startElapsedTimer();
    pollRequest(activeRequestId);
  };

  const handleSelectRecentRequest = (request) => {
    clearTimers();
    setError('');
    setActiveRequestId(request.id);
    setActiveRequestType(request.request_type);

    if (request.status === 'complete') {
      setResult(request);
      setRequestState('complete');
      setElapsedMs(0);
      return;
    }

    setResult(null);
    setRequestState('processing');
    startElapsedTimer();
    pollRequest(request.id);
  };

  const renderResult = () => {
    const aiResponse = result?.ai_response;
    if (!aiResponse) return null;
    if (Array.isArray(aiResponse.leads)) return <LeadResults response={aiResponse} />;
    return <DataResults response={aiResponse} />;
  };

  return (
    <div className="admin-dashboard-modern">
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Agent Winds</h1>
            <p className="dashboard-subtitle">Run AI portal workflows for {SITE_CONFIG.SITE_ID}</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main-content">
        <div className="agent-winds-shell">
          <section className="agent-winds-panel">
            <div className="agent-winds-panel-header">
              <p className="agent-winds-kicker">AI Portal</p>
              <h2>{portalConfig?.agent?.agent_name || SITE_CONFIG.AGENT.NAME}</h2>
              <p>{portalConfig?.agent?.area || 'Request AI-powered services and review the results from one admin workspace.'}</p>
            </div>

            {configLoading ? (
              <LoadingState elapsedMs={0} />
            ) : !portalConfig?.agent ? (
              <div className="agent-winds-empty-result" role="status">
                <h3>Agent Winds is not enabled for this site</h3>
                <p>No AI Portal enrollment was found for site ID {SITE_CONFIG.SITE_ID}.</p>
              </div>
            ) : (
              <form className="agent-winds-form" onSubmit={handleSubmit}>
                <label htmlFor="agent-winds-workflow">Service</label>
                <select
                  id="agent-winds-workflow"
                  value={selectedWorkflowId}
                  onChange={(event) => setSelectedWorkflowId(event.target.value)}
                  disabled={requestState === 'processing'}
                >
                  {portalConfig.workflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.label}
                    </option>
                  ))}
                </select>

                {selectedWorkflow?.description && (
                  <p className="agent-winds-helper-text">{selectedWorkflow.description}</p>
                )}

                <label htmlFor="agent-winds-detail">Request Detail</label>
                <input
                  id="agent-winds-detail"
                  value={requestDetail}
                  onChange={(event) => setRequestDetail(event.target.value)}
                  placeholder="Optional context for the AI worker"
                  disabled={requestState === 'processing'}
                />

                <div className="agent-winds-meta-grid">
                  <span>Site ID: <strong>{SITE_CONFIG.SITE_ID}</strong></span>
                  <span>Requester: <strong>{selectedWorkflow?.requester || 'client'}</strong></span>
                </div>

                {usedToday && requestState !== 'processing' && (
                  <p className="agent-winds-helper-text">
                    You've used {dailyQuota === 1 ? "today's AI task" : `all ${dailyQuota} of today's AI tasks`}. More will be available tomorrow.
                  </p>
                )}

                <button
                  type="submit"
                  className="agent-winds-primary-button"
                  disabled={!selectedWorkflow || requestState === 'processing' || usedToday}
                >
                  {requestState === 'processing' ? 'Processing...' : usedToday ? 'Daily Limit Reached' : 'Run Workflow'}
                </button>
              </form>
            )}
          </section>

          <section className="agent-winds-status-panel" aria-live="polite">
            {requestState === 'idle' && (
              <div className="agent-winds-idle">
                <h2>Ready for Request</h2>
                <p>Select an enrolled service and submit a request. Results will appear here when complete.</p>
              </div>
            )}

            {requestState === 'processing' && (
              <>
                <LoadingState elapsedMs={elapsedMs} requestType={activeRequestType} />
                {activeRequestId && <p className="agent-winds-request-id">Request #{activeRequestId}</p>}
              </>
            )}

            {requestState === 'complete' && (
              <>
                {renderResult()}
                <button type="button" className="agent-winds-secondary-button" onClick={handleReset}>
                  Request Another
                </button>
              </>
            )}

            {(requestState === 'error' || requestState === 'timeout') && (
              <div className="agent-winds-error-card" role="alert">
                <h2>{requestState === 'timeout' ? 'Request Still Processing' : 'Request Failed'}</h2>
                <p>{error}</p>
                <div className="agent-winds-action-row">
                  {activeRequestId && (
                    <button type="button" className="agent-winds-secondary-button" onClick={handleCheckStatusLater}>
                      Check Status Later
                    </button>
                  )}
                  <button type="button" className="agent-winds-primary-button" onClick={handleReset}>
                    Retry
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        <RecentRequests
          requests={recentRequests}
          loading={historyLoading}
          error={historyError}
          onSelect={handleSelectRecentRequest}
        />
      </main>
    </div>
  );
};

export default AgentWinds;
